import React, { useState, useEffect, Component } from "react";
import { useQuery } from "react-query";
import i18next from "i18next";

import plotFunctions from "./data/plot-calculations";

import { Scrolly } from "@learningblocks/interactives/scrollable-components";

const { REACT_APP_SCROLLABLES1_BASE_ID, REACT_APP_LXC_AIRTABLE_API_KEY } =
  process.env;

const fetchAirtableData = async () => {
  if (!REACT_APP_SCROLLABLES1_BASE_ID || !REACT_APP_LXC_AIRTABLE_API_KEY) {
    throw new Error("Airtable environment variables are not configured");
  }

  const response = await fetch(
    `https://api.airtable.com/v0/${REACT_APP_SCROLLABLES1_BASE_ID}/Scrollable Projects?view=Active`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${REACT_APP_LXC_AIRTABLE_API_KEY}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Airtable API error: ${response.status}`);
  }

  return response.json();
};

export class PreviewErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error("ScrollableErrorBoundary caught error:", error, info);
  }

  render() {
    const { hasError, error } = this.state;

    if (hasError) {
      const airtableErrorDetails = error || {};

      return (
        <div className="query-error">
          <div
            style={{
              color: "white",
              fontSize: "20px",
              padding: "20px",
              textAlign: "center",
            }}
          >
            <p
              style={{ color: "white", marginBottom: "15px", fontSize: "24px" }}
            >
              <strong>Project Not Found</strong>
            </p>
            <p style={{ color: "white", marginBottom: "15px" }}>
              If you used the "Preview Link" from{" "}
              <a
                href="https://airtable.com/appFx403k9zFwE9S1/tblK7iuaBTqTa9Sxx/viwplEcqtOl8Pl8C6?blocks=hide"
                target="_blank"
                rel="noreferrer"
                style={{ color: "#4A9EFF" }}
              >
                Scrollables v1.6
              </a>{" "}
              and this takes longer than expected or does not resolve,
            </p>
            <p style={{ color: "white", marginBottom: "15px" }}>
              Try manually entering the project path (meiosis-1) in the URL.
            </p>
            <p style={{ color: "white", marginBottom: "15px" }}>
              Example: "http://localhost:3000/preview-1/?project=meiosis-1"
            </p>
            <p style={{ color: "white", marginBottom: "15px" }}>
              Otherwise you can blame Gareth and get him to take a look.
            </p>
            <p style={{ color: "white" }}>
              <strong>Technical error:</strong>{" "}
              {airtableErrorDetails?.message || "Unknown error"}
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function PreviewScrollable({ lang }) {
  const urlParams = new URLSearchParams(window.location.search);
  const projectParam = urlParams.get("project");
  const normalizedProject = projectParam
    ? projectParam.replace(/%20/g, " ")
    : null;

  const [sceneData, setSceneData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [dataError, setDataError] = useState(null);

  const [stringData, setStringData] = useState(null);
  const [animationLink, setAnimationLink] = useState("");
  const [animationImages, setAnimationImages] = useState(null);
  const [animationData, setAnimationData] = useState(null);
  const [fallbackAnimationData, setFallbackAnimationData] = useState(null);

  const { data: airtableData } = useQuery(
    ["scrollable-preview", normalizedProject],
    fetchAirtableData,
    {
      enabled: !!normalizedProject,
      retry: 1,
      suspense: true,
    }
  );

  useEffect(() => {
    if (airtableData && normalizedProject) {
      try {
        const record = airtableData.records.find(
          (item) => item.fields.Path === normalizedProject
        );

        if (!record) {
          setDataError(
            new Error(`Project "${normalizedProject}" not found in Airtable`)
          );
          return;
        }

        const parsedSceneData = JSON.parse(record.fields.SceneJSON);
        const parsedStringData = JSON.parse(
          record.fields.en_English_TranslationJSON
        );
        const animationUrl = record.fields.Animation?.[0]?.url || "";
        const animImages = record.fields.AnimationImages || [];

        setSceneData(parsedSceneData);
        setStringData(parsedStringData);
        setAnimationLink(animationUrl);
        setAnimationImages(animImages);

        if (animationUrl) {
          fetch(animationUrl)
            .then((response) => response.json())
            .then((animData) => {
              if (animImages && animImages.length > 0) {
                animImages.forEach((image) => {
                  const filename = image.filename.substring(
                    0,
                    image.filename.length - 4
                  );
                  animData.assets?.forEach((asset) => {
                    const name = asset.id.replace("image_", "img_");
                    if (filename === name) {
                      asset.u = "";
                      asset.p = image.url;
                    }
                  });
                });
              }
              setAnimationData(animData);
              setLoaded(true);
            })
            .catch((error) => {
              console.error("[ERROR] Failed to load preview animation:", error);
            });
        } else {
          setLoaded(true);
        }
      } catch (error) {
        console.error("[ERROR] Failed to parse Airtable data:", error);
        setDataError(error);
      }
    }
  }, [airtableData, normalizedProject]);

  useEffect(() => {
    const fallbackLocation =
      window.location.origin +
      window.location.pathname +
      "locales/en/animation.json";

    fetch(fallbackLocation)
      .then((response) => response.json())
      .then((data) => {
        setFallbackAnimationData(data);
      })
      .catch((error) => {
        console.error("[ERROR] Failed to load fallback animation:", error);
      });
  }, []);

  useEffect(() => {
    if (lang && loaded) {
      i18next.changeLanguage(lang);

      const animLocation =
        window.location.origin +
        window.location.pathname +
        `locales/${lang}/animation.json`;

      fetch(animLocation)
        .then((response) => response.json())
        .then((animData) => {
          if (animationImages && animationImages.length > 0) {
            animationImages.forEach((image) => {
              const filename = image.filename.substring(
                0,
                image.filename.length - 4
              );
              animData.assets?.forEach((asset) => {
                const name = asset.id.replace("image_", "img_");
                if (filename === name) {
                  asset.u = "";
                  asset.p = image.url;
                }
              });
            });
          }
          setAnimationData(animData);
        })
        .catch((error) => {
          console.error(`[ERROR] Failed to load ${lang} animation:`, error);
        });
    }
  }, [lang, loaded, animationImages]);

  if (dataError) {
    throw dataError;
  }

  if (!normalizedProject) {
    return (
      <div className="query-error">
        <div
          style={{
            color: "white",
            fontSize: "20px",
            padding: "20px",
            textAlign: "center",
          }}
        >
          <p style={{ color: "white", marginBottom: "15px", fontSize: "24px" }}>
            <strong>Missing Project Parameter</strong>
          </p>
          <p style={{ color: "white", marginBottom: "15px" }}>
            Missing or invalid project parameter.
          </p>
          <p style={{ color: "white", marginBottom: "15px" }}>
            Please add a project parameter to the URL:
          </p>
          <p style={{ color: "white" }}>
            Example:{" "}
            <code style={{ color: "#4A9EFF" }}>?project=meiosis-1</code>
          </p>
        </div>
      </div>
    );
  }

  if (!sceneData || animationData === null) {
    return (
      <div className="fallback-loading">
        <div className="loading-animation1">.</div>
        <div className="loading-animation2">.</div>
        <div className="loading-animation3">.</div>
      </div>
    );
  }

  return (
    <Scrolly
      animationLink={animationLink}
      animationData={
        animationData === undefined || animationData === null
          ? fallbackAnimationData
          : animationData
      }
      sceneData={sceneData}
      stringData={stringData}
      AnimationImages={animationImages}
      plotFunctions={plotFunctions}
    />
  );
}

export default PreviewScrollable;
